/**
 * S3 Upload Utility Functions
 * Handles file uploads to S3 using presigned URLs
 */

import { makeApiCall } from './api';
import { API_PATHS } from '../config/api-endpoints';

/**
 * Request interface for getting presigned upload URL
 */
export interface GetPresignedUploadUrlRequest {
    fileName: string;
    fileSize: number;
    contentType: string;
}

/**
 * Response interface for presigned upload URL
 */
export interface GetPresignedUploadUrlResponse {
    uploadUrl: string;
    s3Key: string;  // Changed from fileKey to match actual response
    expiresAt?: string;
}

/**
 * Result of an S3 upload operation
 */
export interface S3UploadResult {
    success: boolean;
    fileKey?: string;
    error?: any;
}

/**
 * Get a presigned URL for uploading a file to S3
 * @param file - The file to upload
 * @returns Promise with presigned URL response
 */
export async function getPresignedUploadUrl(file: File): Promise<GetPresignedUploadUrlResponse> {
    const request: GetPresignedUploadUrlRequest = {
        fileName: file.name,
        fileSize: file.size,
        contentType: file.type || 'application/octet-stream'
    };
    
    try {
        const response = await makeApiCall(
            API_PATHS.DOCUMENTS.PRESIGNED_UPLOAD,
            'POST',
            request
        );
        return response;
    } catch (error) {
        console.error('Failed to get presigned URL:', error);
        throw error;
    }
}

/**
 * Upload a file to S3 using a presigned URL
 * @param file - The file to upload
 * @param uploadUrl - The presigned URL from the backend
 * @returns Promise with upload result
 */
export async function uploadToS3(file: File, uploadUrl: string): Promise<S3UploadResult> {
    try {
        
        // The presigned URL already contains all necessary parameters including Content-Type
        // We should NOT add additional headers as they might conflict with the signature
        const uploadResponse = await fetch(uploadUrl, {
            method: 'PUT',
            body: file,
            // Don't add Content-Type header - it's already in the presigned URL
        });
        
        if (!uploadResponse.ok) {
            // Try to get error details
            const errorText = await uploadResponse.text().catch(() => '');
            console.error('S3 upload error response:', errorText);
            throw new Error(`Upload failed with status: ${uploadResponse.status}. ${errorText}`);
        }
        
        return {
            success: true
        };
    } catch (error: any) {
        console.error('S3 upload failed:', error);
        
        // Check if it's a CORS error
        if (error.message?.includes('CORS') || error.message?.includes('Failed to fetch')) {
            console.error('CORS error detected. The S3 bucket needs CORS configuration to allow uploads from this origin.');
        }
        
        return {
            success: false,
            error
        };
    }
}

/**
 * Complete file upload process: get presigned URL and upload to S3
 * @param file - The file to upload
 * @returns Promise with the S3 file key
 */
export async function uploadFileToS3(file: File): Promise<string> {
    try {
        // Step 1: Get presigned URL from backend
        const { uploadUrl, s3Key } = await getPresignedUploadUrl(file);
        
        // Step 2: Upload file to S3
        const uploadResult = await uploadToS3(file, uploadUrl);
        
        if (!uploadResult.success) {
            // If CORS error, provide helpful message
            if (uploadResult.error?.message?.includes('CORS')) {
                throw new Error('S3 bucket CORS configuration needed. Please contact the backend team to configure CORS for uploads.');
            }
            throw new Error(uploadResult.error?.message || 'Failed to upload file to S3');
        }
        
        // Return the S3 key for the uploaded file
        return s3Key;
    } catch (error) {
        console.error('File upload process failed:', error);
        throw error;
    }
}

/**
 * Upload multiple files to S3
 * @param files - Array of files to upload
 * @returns Promise with array of S3 keys
 */
export async function uploadMultipleFilesToS3(files: File[]): Promise<string[]> {
    try {
        const uploadPromises = files.map(file => uploadFileToS3(file));
        const results = await Promise.all(uploadPromises);
        return results;
    } catch (error) {
        console.error('Multiple file upload failed:', error);
        throw error;
    }
}
